<!-- What the host shows of the issue itself: the form or the read-only fields. -->
<ng-template #details><ng-content /></ng-template>

@if (enabled) {
  <!-- Same tabs as the punch list item detail (punchlist-item-detail.component.html) -->
  <mat-tab-group
    (selectedIndexChange)="selectTab($event)"
    [selectedIndex]="selectedTabIndex"
    class="contents-tab-group"
    data-testid="issue-workflow-tabs">
    <mat-tab label="Details">
      <div class="details-content pt-[20px]">
        <!-- Status: the one pill of the issue, as the punch list item detail has its Status row -->
        @if (showStatus && chip) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]" data-testid="issue-workflow-status">
            <div class="label w-full">Status</div>
            <div class="value w-full">
              <div [ngClass]="'tone-' + chip.tone" class="status-chip px-[14px] rounded-full w-fit">
                {{ chip.label }}
              </div>
            </div>
          </div>
        }

        <ng-container *ngTemplateOutlet="details" />

        <section class="issue-workflow" data-testid="issue-workflow-panel">
          <!-- Everyone the issue was forwarded to (send and forward recipients), each person once. -->
          @if (assignedTo.length) {
            <div class="row-details flex flex-col gap-[4px] mb-[20px]" data-testid="issue-workflow-assigned-to">
              <div class="label w-full">Forwarded to</div>
              @for (assignee of assignedTo; track assignee.person.id || assignee.person.email) {
                <div class="value w-full">
                  <div class="text-type-11">{{ personLabel(assignee.person) }}</div>
                  @if (assignee.assigned_at) {
                    <div class="text-type-14 text-[#60698B]">
                      {{ assignee.assigned_at | date:'MMM d, y' }}@if (assignee.assigned_by_name) { &middot; by {{ assignee.assigned_by_name }} }
                    </div>
                  }
                </div>
              }
            </div>
          }

          <!--
            The action area of the claim / punch list item detail: the pick list
            starts empty; picking an action shows its inputs and Submit, clearing
            it hides them again (the host shows its Save bar only meanwhile).
          -->
          @if (actionOptions.length) {
            <hr class="hr-line" />
            <app-dropdown
              [enableClearSelection]="true"
              [options]="actionOptions"
              [parentFormGroup]="actionForm"
              class="w-full mb-[20px]"
              controlName="action"
              data-testid="issue-workflow-action"
              placeholder="Select an action"
              label="actions"
            />

            @if (selectedAction) {
              @for (field of actionFields; track selectedAction.state + '/' + field.input.name) {
                @switch (field.kind) {
                  @case ('multi-choice') {
                    <app-dropdown
                      [enableSearch]="true"
                      [enableMultiple]="true"
                      [showCount]="true"
                      [selectionString]="'users'"
                      [loading]="loadingPeople"
                      [options]="inputOptions[field.input.name]"
                      [parentFormGroup]="inputForm"
                      [controlName]="field.input.name"
                      [label]="field.label"
                      [attr.data-testid]="'issue-workflow-input-' + field.input.name"
                      class="w-full mb-[20px]"
                      placeholder="Select people"
                    />
                  }
                  @case ('choice') {
                    <app-dropdown
                      [enableClearSelection]="!field.input.required"
                      [loading]="loadingPeople && !field.input.options?.length"
                      [options]="inputOptions[field.input.name]"
                      [parentFormGroup]="inputForm"
                      [controlName]="field.input.name"
                      [label]="field.label"
                      [attr.data-testid]="'issue-workflow-input-' + field.input.name"
                      class="w-full mb-[20px]"
                      placeholder="Select"
                    />
                  }
                  @case ('text') {
                    <app-text-area
                      [parentFormGroup]="inputForm"
                      [controlName]="field.input.name"
                      [label]="field.label"
                      [attr.data-testid]="'issue-workflow-input-' + field.input.name"
                      class="w-full mb-[20px]"
                    />
                  }
                  @case ('files') {
                    <app-pictures-section-with-tags
                      (onDelete)="deleteActionPicture($event)"
                      [add]="true"
                      [pictures]="actionMediaFiles"
                      [tags]="'actions-attachments'"
                      [label]="field.label"
                      [attr.data-testid]="'issue-workflow-input-' + field.input.name"
                      class="pictures-section mb-[40px]"
                    />
                  }
                }
              }

              @if (actionError) {
                <div class="action-error text-type-14 mb-[20px]" data-testid="issue-workflow-action-error" role="alert">
                  {{ actionError }}
                </div>
              }

              <div class="action-section mb-[5px] flex flex-row gap-[20px] w-full">
                <app-primary-button
                  (handleClick)="executeAction()"
                  [isDisabled]="!canSubmit"
                  [isLoading]="acting"
                  class="flex-1"
                  data-testid="issue-workflow-submit">
                  Submit
                </app-primary-button>
              </div>
            }
          }
        </section>
      </div>
    </mat-tab>

    <mat-tab label="Discussion">
      <div class="discussion-content pt-[20px]">
        @if (historyFailed) {
          <div class="text-type-14 text-[#60698B] py-[20px]" data-testid="issue-workflow-history-error">
            The history could not be loaded.
            <button (click)="retryHistory()" class="retry-link" type="button">Try again</button>
          </div>
        } @else if (!loadingHistory || history.length) {
          <app-workflow-discussion [entries]="history" recipientsLabel="Forwarded to" data-testid="issue-workflow-history" />
        }
      </div>
    </mat-tab>
  </mat-tab-group>
} @else {
  <!-- Off the contractor workflow: the host's content as it is, and nothing else. -->
  <ng-container *ngTemplateOutlet="details" />
}
